"use client";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const WO_TYPES = [
  {
    value: "piecework",
    label: "Piecework",
    hint: "Charged via piecework sheets",
    color: "orange",
  },
  {
    value: "hourly",
    label: "Hourly",
    hint: "Charged via timesheets",
    color: "blue",
  },
  {
    value: "subcontractor",
    label: "Subcontractor",
    hint: "External subcontractor work",
    color: "gray",
  },
];

// ── Item Search Modal (Labour items, category=L) ───────────────────────────
function ItemSearchModal({ onSelect, onClose, token }) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(
    async (q) => {
      if (!q.trim()) {
        setItems([]);
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/item-database?search=${encodeURIComponent(q)}&limit=50&category=L`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setItems(res.data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h3 className="text-base font-semibold text-white">
            Select Labour Item from Database
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-3">
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by labour code or description…"
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
          />
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-1">
          {loading && (
            <p className="text-gray-400 text-sm py-4 text-center">Searching…</p>
          )}
          {!loading && items.length === 0 && query && (
            <p className="text-gray-500 text-sm py-4 text-center">
              No labour items found.
            </p>
          )}
          {items.map((item) => (
            <button
              key={item._id}
              onClick={() => onSelect(item)}
              className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-orange-400">
                  {item.itemCode}
                </span>
                <span className="text-xs text-gray-500">
                  ${fmt(item.defaultCost)}/{item.uom || "hr"}
                </span>
              </div>
              <p className="text-sm text-gray-200 mt-0.5">{item.description}</p>
              {item.defaultHoursPerUnit && (
                <p className="text-xs text-blue-400 mt-0.5">
                  {item.defaultHoursPerUnit} hrs/{item.uom || "unit"}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Line Item Row ─────────────────────────────────────────────────────────
function LineItemRow({ item, idx, woType, onUpdate, onRemove }) {
  const updateItem = (field, value) => onUpdate(idx, field, value);
  const isHourly = woType === "hourly";
  const isSubcontractor = woType === "subcontractor";

  const lineTotal = isHourly
    ? (parseFloat(item.laborHours) || 0) * (parseFloat(item.unitRate) || 0)
    : (parseFloat(item.quantity) || 0) * (parseFloat(item.unitRate) || 0);

  return (
    <div className="bg-gray-700 rounded-xl border border-gray-600 p-3 space-y-2.5">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono text-orange-400">
            {item.itemCode}
          </p>
          <p className="text-sm text-gray-200 font-medium leading-snug">
            {item.description}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {isHourly
              ? "Hourly — charged via timesheets"
              : isSubcontractor
                ? "Subcontractor — external work"
                : "Piecework — charged via piecework sheets"}
          </p>
        </div>
        <button
          onClick={() => onRemove(idx)}
          className="text-gray-500 hover:text-red-400 text-xl leading-none shrink-0"
        >
          ×
        </button>
      </div>

      {/* Row 1: Qty · Unit Rate · Hours · Line total */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            {isHourly ? "Est. Hours" : "Qty (units)"}
          </label>
          <input
            type="number"
            value={isHourly ? item.laborHours : item.quantity}
            min="0"
            step={isHourly ? "0.5" : "1"}
            onChange={(e) =>
              updateItem(isHourly ? "laborHours" : "quantity", e.target.value)
            }
            className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            {isHourly ? "Hourly Rate ($/hr)" : "Unit Rate ($/unit)"}
          </label>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
              $
            </span>
            <input
              type="number"
              value={item.unitRate}
              min="0"
              step="0.01"
              onChange={(e) => updateItem("unitRate", e.target.value)}
              className="w-full pl-5 pr-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Est. Hours only shown for piecework (not hourly — hours IS qty there) */}
        {!isHourly && (
          <div>
            <label className="text-[10px] text-gray-400 mb-0.5 block">
              Est. Hours
            </label>
            <input
              type="number"
              value={item.laborHours}
              min="0"
              step="0.5"
              onChange={(e) => updateItem("laborHours", e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
            />
          </div>
        )}

        <div
          className={cls(
            "flex flex-col justify-end",
            !isHourly ? "" : "col-span-2",
          )}
        >
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            Line Total
          </label>
          <div className="px-2 py-1.5 text-xs bg-gray-600/50 border border-gray-600 rounded-lg text-orange-300 font-mono font-semibold text-right">
            ${fmt(lineTotal)}
          </div>
        </div>
      </div>

      {/* Row 2: UOM · Section · Notes */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">UOM</label>
          <input
            type="text"
            value={item.uom}
            placeholder="e.g. SF, LF, EA, hr"
            onChange={(e) => updateItem("uom", e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            Section #
          </label>
          <input
            type="text"
            value={item.sectionNumber}
            placeholder="e.g. 01"
            onChange={(e) => updateItem("sectionNumber", e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="text-[10px] text-gray-400 mb-0.5 block">
            Notes
          </label>
          <input
            type="text"
            value={item.notes}
            placeholder="Optional notes…"
            onChange={(e) => updateItem("notes", e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-gray-600/50 border border-gray-600 rounded-lg text-gray-400 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
}

// ── WO Type + Union Selector ──────────────────────────────────────────────
function WOTypeUnionSelector({
  woType,
  union,
  unions,
  onTypeChange,
  onUnionChange,
}) {
  const isSubcontractor = woType === "subcontractor";

  const unionOptions = useMemo(() => {
    const companyUnions = (unions || []).map((u) => ({
      value: u.title,
      label: u.title,
    }));
    return companyUnions.length > 0
      ? [...companyUnions, { value: "non-union", label: "Non-Union" }]
      : [{ value: "non-union", label: "Non-Union" }];
  }, [unions]);

  const isUnion = union !== "non-union";

  return (
    <div className="space-y-3">
      {/* Type selector */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">
          Work Order Type *
        </label>
        <div className="grid grid-cols-3 gap-2">
          {WO_TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                onTypeChange(t.value);
                // clear union when switching to subcontractor
                if (t.value === "subcontractor") onUnionChange("non-union");
              }}
              className={cls(
                "px-3 py-2.5 rounded-lg border text-left transition-colors",
                woType === t.value
                  ? t.value === "hourly"
                    ? "bg-blue-500/20 border-blue-500 text-blue-300"
                    : t.value === "subcontractor"
                      ? "bg-gray-500/30 border-gray-400 text-gray-200"
                      : "bg-orange-500/20 border-orange-500 text-orange-300"
                  : "bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300",
              )}
            >
              <p className="text-xs font-semibold">{t.label}</p>
              <p className="text-[10px] mt-0.5 opacity-70">{t.hint}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Union selector — hidden for subcontractor */}
      {!isSubcontractor && (
        <div>
          <label className="block text-xs text-gray-400 mb-2">
            Union / Labour Agreement
          </label>
          <div className="flex flex-wrap gap-2">
            {unionOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onUnionChange(opt.value)}
                className={cls(
                  "px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors",
                  union === opt.value
                    ? opt.value === "non-union"
                      ? "bg-gray-600 border-gray-400 text-gray-200"
                      : "bg-teal-500/20 border-teal-500 text-teal-300"
                    : "bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {isUnion && (
            <p className="text-[11px] text-teal-400 mt-1.5">
              ✓ Union rates apply — burden calculations will use the{" "}
              <span className="font-semibold">{union}</span> agreement
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function CreateWO3() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  // Header fields
  const [title, setTitle] = useState("");
  const [bidArea, setBidArea] = useState("");
  const [woType, setWoType] = useState("piecework");
  const [union, setUnion] = useState("non-union");
  const [notes, setNotes] = useState("");

  // Items
  const [items, setItems] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  // Data from API
  const [existingFloors, setExistingFloors] = useState([]);
  const [unions, setUnions] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isHourly = woType === "hourly";
  const isSubcontractor = woType === "subcontractor";

  // Load existing floors + company unions
  useEffect(() => {
    const load = async () => {
      try {
        const [woRes, unionRes] = await Promise.all([
          axios.get(`${API}/workorders3/project/${projectId}`, { headers }),
          axios.get(`${API}/company-profile/unions`, { headers }),
        ]);
        const floors = [
          ...new Set(
            (woRes.data.workOrders || []).map((w) => w.bidArea).filter(Boolean),
          ),
        ].sort();
        setExistingFloors(floors);

        const fetchedUnions = unionRes.data || [];
        setUnions(fetchedUnions);
        // Default union to first company union if available
        if (fetchedUnions.length > 0) {
          setUnion(fetchedUnions[0].title);
        }
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [projectId, headers]);

  // When type changes to subcontractor, reset union
  const handleTypeChange = (newType) => {
    setWoType(newType);
    if (newType === "subcontractor") setUnion("non-union");
  };

  // Add item from DB
  const addItem = (dbItem) => {
    setItems((prev) => [
      ...prev,
      {
        itemDatabaseId: dbItem._id,
        itemCode: dbItem.itemCode || "",
        description: dbItem.description || "",
        uom: dbItem.uom || "",
        unitRate: parseFloat(dbItem.defaultCost) || 0,
        quantity: isHourly ? 0 : 1,
        laborHours: isHourly ? 1 : parseFloat(dbItem.defaultHoursPerUnit) || 0,
        sectionNumber: "",
        notes: "",
      },
    ]);
    setShowSearch(false);
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)),
    );
  };

  const removeItem = (idx) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const grandTotal = useMemo(
    () =>
      items.reduce((s, i) => {
        const qty = isHourly
          ? parseFloat(i.laborHours) || 0
          : parseFloat(i.quantity) || 0;
        return s + qty * (parseFloat(i.unitRate) || 0);
      }, 0),
    [items, isHourly],
  );

  const totalHours = useMemo(
    () => items.reduce((s, i) => s + (parseFloat(i.laborHours) || 0), 0),
    [items],
  );

  const handleSave = async () => {
    if (!title.trim()) return setError("Title is required.");
    if (!bidArea.trim()) return setError("Floor / Bid Area is required.");

    setSaving(true);
    setError("");
    try {
      const payload = {
        title: title.trim(),
        bidArea: bidArea.trim(),
        type: woType,
        union: isSubcontractor ? "non-union" : union,
        sectionNumbers: [
          ...new Set(items.map((i) => i.sectionNumber).filter(Boolean)),
        ],
        sectionNumber: items[0]?.sectionNumber || "",
        notes,
        lineItems: items.map((item) => {
          const qty = isHourly ? 0 : parseFloat(item.quantity) || 0;
          const hrs = parseFloat(item.laborHours) || 0;
          const rate = parseFloat(item.unitRate) || 0;
          const total = isHourly ? hrs * rate : qty * rate;
          return {
            itemDatabaseId: item.itemDatabaseId,
            itemCode: item.itemCode,
            description: item.description,
            uom: item.uom,
            quantity: qty,
            unitRate: rate,
            laborHours: hrs,
            total,
            sectionNumber: item.sectionNumber || "",
            bidArea: bidArea.trim(),
            estimatedQty: qty,
            estimatedTotal: total,
            source: "manual",
            notes: item.notes,
          };
        }),
      };

      await axios.post(`${API}/workorders3/project/${projectId}`, payload, {
        headers,
      });
      navigate(`/Projects/${projectId}?tab=Work Orders`);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Derived display values
  const selectedType = WO_TYPES.find((t) => t.value === woType);
  const isUnion = !isSubcontractor && union !== "non-union";

  return (
    <div className="mx-auto px-4 py-6 space-y-6">
      {showSearch && (
        <ItemSearchModal
          token={token}
          onSelect={addItem}
          onClose={() => setShowSearch(false)}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/Projects/${projectId}?tab=Work Orders`)}
          className="px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <div>
          <h1 className="text-lg font-bold text-white">New Work Order</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            Manual WO — items added from the labour database
          </p>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-900/50 border border-red-700 rounded-lg text-sm text-red-300">
          {error}
        </div>
      )}

      {/* WO header fields */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-white">WO Details</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Floor 3 — Finishing Labour"
              className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">
              Floor / Bid Area *
              {existingFloors.length > 0 && (
                <span className="ml-2 text-gray-500">— or pick existing:</span>
              )}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={bidArea}
                onChange={(e) => setBidArea(e.target.value)}
                placeholder="e.g. Floor 3"
                className="flex-1 px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
              />
              {existingFloors.length > 0 && (
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) setBidArea(e.target.value);
                  }}
                  className="px-2 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-400 focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Existing…</option>
                  {existingFloors.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Type + Union selector */}
        <WOTypeUnionSelector
          woType={woType}
          union={union}
          unions={unions}
          onTypeChange={handleTypeChange}
          onUnionChange={setUnion}
        />

        {/* Active type + union badge summary */}
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cls(
              "px-2 py-1 text-[11px] font-semibold rounded",
              woType === "hourly"
                ? "bg-blue-800/60 text-blue-200"
                : woType === "subcontractor"
                  ? "bg-gray-600/60 text-gray-300"
                  : "bg-orange-800/60 text-orange-200",
            )}
          >
            {selectedType?.label}
          </span>
          {isUnion && (
            <span className="px-2 py-1 text-[11px] font-semibold rounded bg-teal-800/60 text-teal-200">
              {union}
            </span>
          )}
          <span className="text-xs text-gray-500">{selectedType?.hint}</span>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Internal notes…"
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
          />
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">
            Line Items
            {items.length > 0 && (
              <span className="ml-2 text-xs text-gray-500 font-normal">
                {items.length} item{items.length !== 1 ? "s" : ""} ·{" "}
                <span className="text-orange-300">${fmt(grandTotal)}</span>
                {totalHours > 0 && (
                  <span className="ml-2 text-blue-400">
                    {totalHours.toFixed(1)} hrs
                  </span>
                )}
              </span>
            )}
          </h2>
          <button
            onClick={() => setShowSearch(true)}
            className="px-4 py-2 text-sm font-semibold bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            + Add Item
          </button>
        </div>

        {!items.length && (
          <div className="border border-dashed border-gray-600 rounded-xl py-10 text-center">
            <p className="text-sm text-gray-500 mb-3">No items yet.</p>
            <button
              onClick={() => setShowSearch(true)}
              className="px-4 py-2 text-sm font-semibold bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Search Labour Database
            </button>
          </div>
        )}

        {items.map((item, idx) => (
          <LineItemRow
            key={idx}
            item={item}
            idx={idx}
            woType={woType}
            onUpdate={updateItem}
            onRemove={removeItem}
          />
        ))}

        {/* Grand total bar */}
        {items.length > 0 && (
          <div className="flex items-center justify-between bg-gray-800 rounded-xl border border-gray-700 px-5 py-3">
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span>{items.length} items</span>
              {totalHours > 0 && (
                <span className="text-blue-400">
                  {totalHours.toFixed(1)} estimated hrs
                </span>
              )}
              {isUnion && (
                <span className="text-teal-400 text-xs">{union}</span>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Grand Total</p>
              <p className="text-xl font-bold text-orange-300 font-mono">
                ${fmt(grandTotal)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Save / Cancel */}
      <div className="flex gap-3 pt-2 border-t border-gray-700">
        <button
          onClick={handleSave}
          disabled={saving}
          className={cls(
            "px-6 py-2.5 text-sm font-semibold rounded-lg text-white transition-colors",
            saving
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600",
          )}
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full" />
              Creating WO…
            </span>
          ) : (
            "Create Work Order"
          )}
        </button>
        <button
          onClick={() => navigate(`/Projects/${projectId}?tab=Work Orders`)}
          className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-600 hover:border-gray-400 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
